import * as React from "react";
import { useState } from "react";
import { DateTime } from "luxon";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import {
    Box,
    IconButton,
    InputAdornment,
    Popover,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CalendarMonth, Clear } from "@mui/icons-material";

/** Selection modes of the date range picker */
export const MODE_RANGE = "range"; // Start + end dates (selecting only a start date is also allowed)
export const MODE_UP_TO = "upTo"; // Only an end date

const MODE_LABELS = {
    [MODE_RANGE]: "Range",
    [MODE_UP_TO]: "Up to",
};

// Range of months available in the calendar's month/year dropdowns
const START_MONTH = new Date(2000, 0);
const END_MONTH = new Date(new Date().getFullYear() + 1, 11);

/** Converts a JS Date (used by DayPicker) into a Luxon DateTime */
const toDateTime = (date) =>
    date ? DateTime.fromJSDate(date).startOf("day") : null;

/** Converts a Luxon DateTime into a JS Date */
const toJSDate = (dateTime) => (dateTime ? dateTime.toJSDate() : undefined);

/** Returns the mode that best matches the current value */
function initialMode(from, to, modes) {
    if (!from && to && modes.includes(MODE_UP_TO)) return MODE_UP_TO;
    return modes[0];
}

/**
 * A single input box for selecting a date range (or an open-ended range - only a start / end date).
 *
 * @param label label of the input box
 * @param value array of [from, to] Luxon DateTime values (either can be null), or an empty array
 * @param onChange callback receiving the new [from, to] array (or an empty array when cleared)
 * @param format date display format
 * @param modes (optional) selection modes to allow (MODE_RANGE / MODE_UP_TO) - the first one is
 *      the default mode. If there's more than one mode, the user can switch between them.
 * @param className (optional) class name of the input box
 * @param error (optional) if true, the input box is displayed in an error state
 * @param helperText (optional) text displayed below the input box (e.g. error message)
 */
export default function DateRangePicker({
    label,
    value,
    onChange,
    format,
    modes = [MODE_RANGE, MODE_UP_TO],
    className = "w-[260px]",
    error = false,
    helperText = null,
}) {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const [from, to] = value || [];
    const [mode, setMode] = useState(() => initialMode(from, to, modes));

    const close = () => setAnchorEl(null);

    let displayValue = "";
    if (from && to) {
        displayValue = `${from.toFormat(format)} – ${to.toFormat(format)}`;
    } else if (from) {
        displayValue = `From ${from.toFormat(format)}`;
    } else if (to) {
        displayValue = `Up to ${to.toFormat(format)}`;
    }

    /** A day was clicked in range mode - the first click picks the start date, the second one the end date */
    const onRangeDayClick = (date) => {
        const day = toDateTime(date);
        if (from && !to) {
            // Complete the range (a single day range is allowed)
            onChange(day < from ? [day, from] : [from, day]);
            close();
        } else {
            // Start a new range - until an end date is picked, this is a "from" date only
            onChange([day, null]);
        }
    };

    /** A day was clicked in "up to" mode - only an end date */
    const onUpToSelect = (date) => {
        onChange(date ? [null, toDateTime(date)] : []);
        if (date) close();
    };

    const dayPickerProps = {
        captionLayout: "dropdown",
        startMonth: START_MONTH,
        endMonth: END_MONTH,
        defaultMonth: toJSDate(from || to),
    };

    let dayPicker;
    if (mode === MODE_RANGE) {
        dayPicker = (
            <DayPicker
                mode="range"
                selected={
                    from
                        ? { from: toJSDate(from), to: toJSDate(to) }
                        : undefined
                }
                onSelect={(range, triggerDate) => onRangeDayClick(triggerDate)}
                {...dayPickerProps}
            />
        );
    } else {
        dayPicker = (
            <DayPicker
                mode="single"
                selected={from ? undefined : toJSDate(to)}
                onSelect={onUpToSelect}
                {...dayPickerProps}
            />
        );
    }

    return (
        <>
            <TextField
                label={label}
                value={displayValue}
                className={className}
                error={error}
                helperText={helperText}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            {displayValue ? (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        // Don't open the calendar when clearing
                                        e.stopPropagation();
                                        onChange([]);
                                    }}
                                >
                                    <Clear fontSize="small" />
                                </IconButton>
                            ) : (
                                <CalendarMonth fontSize="small" />
                            )}
                        </InputAdornment>
                    ),
                }}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box
                    className="p-3"
                    sx={{
                        "--rdp-accent-color": theme.palette.primary.main,
                        "--rdp-accent-background-color": alpha(
                            theme.palette.primary.main,
                            0.12,
                        ),
                    }}
                >
                    {modes.length > 1 && (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={mode}
                            onChange={(e, newMode) =>
                                newMode && setMode(newMode)
                            }
                        >
                            {modes.map((m) => (
                                <ToggleButton key={m} value={m}>
                                    {MODE_LABELS[m]}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}
                    {dayPicker}
                </Box>
            </Popover>
        </>
    );
}
